import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs, buildArticle } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/metallic-v3.jpg";

export const Route = createFileRoute("/blog/metallic-epoxy-design-ideas")({
  head: () => ({
    meta: [
      { title: "10 Metallic Epoxy Design Ideas for Custom Garages | MLC Epoxy Floors" },
      { name: "description", content: "Color palettes, pattern direction, and inspiration for your next metallic epoxy garage floor — from copper canyon to midnight blue." },
      { property: "og:title", content: "10 Metallic Epoxy Design Ideas for Custom Garages" },
      { property: "og:description", content: "Color palettes and pattern direction for your next floor." },
      { property: "og:url", content: "/blog/metallic-epoxy-design-ideas" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/blog/metallic-epoxy-design-ideas" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildArticle({
          title: "10 Metallic Epoxy Design Ideas for Custom Garages",
          description: "Color palettes, pattern direction, and inspiration for your next metallic epoxy garage floor — from copper canyon to midnight blue.",
          slug: "metallic-epoxy-design-ideas",
          datePublished: "2026-02-28",
        })),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildBreadcrumbs([
          { name: "Home", href: "/" },
          { name: "Blog", href: "/blog" },
          { name: "Metallic Epoxy Design Ideas", href: "/blog/metallic-epoxy-design-ideas" },
        ])),
      },
    ],
  }),
  component: () => (
    <ResourceArticle
      eyebrow="Feb 28, 2026"
      title="10 Metallic Epoxy Design Ideas for Custom Garages"
      intro="Metallic epoxy is the most expressive coating system on the market — no two floors are alike. Here are ten direction-setting palettes our team is loving right now."
      image={heroImg}
      backHref="/blog"
      backLabel="All Articles"
      sections={[
        {
          type: "list",
          heading: "Ten Palette Directions",
          items: [
            { label: "1. Liquid Copper", desc: "Deep bronze base with copper highlights — dramatic against black cars and stainless tool chests." },
            { label: "2. Black Pearl", desc: "Near-black with subtle silver shimmer. Reads as luxury showroom rather than garage." },
            { label: "3. Midnight Aurora", desc: "Deep navy with electric blue and teal movement. Unexpected and modern." },
            { label: "4. Aged Bronze", desc: "Warm patina effect — perfect for craftsman and vintage-styled spaces." },
            { label: "5. Titanium Mist", desc: "Cool silver-gray that brightens dim spaces without showing dust like white." },
            { label: "6. Volcanic", desc: "Charcoal base with rust-orange and amber veining. Bold, industrial." },
            { label: "7. Galaxy", desc: "Deep purple with silver and gold flecks — surprisingly popular for home theaters and finished basements." },
            { label: "8. Sand Dune", desc: "Warm tan with cream movement — pairs beautifully with light wood cabinetry." },
            { label: "9. Forest Floor", desc: "Deep green base with brown and gold accents. Unexpected in a garage; perfect for wine cellars." },
            { label: "10. Pewter", desc: "Muted gunmetal with soft white highlights. The safe-but-elegant choice." },
          ],
        },
        {
          type: "prose",
          heading: "Picking the Right Palette",
          body: "Metallic colors look very different in person than in photos. The pigments are reflective, so lighting changes everything. We always recommend viewing physical sample boards in your actual garage, ideally at the time of day you'll use the space most.",
        },
        {
          type: "prose",
          heading: "Patterns vs Pure Swirl",
          body: "Within metallic, there are two install techniques: pure swirl (random, organic movement) and controlled patterns (lightning strikes, river effects, defined movement). Pure swirl is timeless. Pattern work is striking but more polarizing — make sure you love it before committing.",
        },
        {
          type: "links",
          heading: "Related On This Site",
          intro: "Ready to take a metallic palette from idea to install.",
          links: [
            { href: "/services/metallic-epoxy-flooring", label: "Metallic Epoxy Flooring Service", desc: "Pricing, process, and palette ranges for our metallic system." },
            { href: "/gallery?category=EPOXY+FLAKE+SYSTEM", label: "EPOXY FLAKE SYSTEM Gallery", desc: "See completed projects across the Calgary region." },
          ],
        },
      ]}
    />
  ),
});
